/* eslint-disable import/extensions */
/* eslint-disable import/no-unresolved */
import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Paper,
  Divider,
  Stack,
  CircularProgress,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableFooter,
  Chip,
  Tooltip,
  TextField,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  Inventory,
  Assignment,
  Notes,
  CheckCircle,
  Cancel,
  HourglassTop,
  ShoppingCart,
  LocalShipping,
  CheckCircleOutline,
  ReceiptLong,
  Timeline as TimelineIcon,
  ArrowForward,
  RequestQuote,
  Star,
  TouchApp,
  PictureAsPdf,
  HowToReg,
  Edit,
  Save,
  Close,
} from '@mui/icons-material';

import api from '../services/api';

export interface PurchaseRequestDetailData {
  empresa: number;
  filial: number;
  solicitacao: number;
  sequencia: number;
  dataSolicitacao: string;
  horaSolicitacao?: number;
  dataPrevista?: string;
  produtoServico: 'P' | 'S';
  produto?: string;
  descricaoProduto?: string;
  complementoProduto?: string;
  derivacao?: string;
  servico?: string;
  descricaoServico?: string;
  quantidadeSolicitada: number;
  quantidadeAprovada: number;
  quantidadeCancelada: number;
  quantidade: number;
  situacaoAprovacao: 'ANA' | 'APR' | 'REP' | 'CAN';
  codUsuarioSolicitante: number;
  nomeUsuarioSolicitante?: string;

  codUsuarioAprovador?: number;
  nomeUsuarioAprovador?: string;
  dataAprovacao?: string;
  horaAprovacao?: number;

  codUsuarioCancelador?: number;
  nomeUsuarioCancelador?: string;
  dataCancelamento?: string;
  horaCancelamento?: number;

  observacaoSolicitacao?: string;
  motivoCancelamento?: string;
  centroCusto?: string;
  descricaoCentroCusto?: string;
  centroCustoAplicacao?: string;
  descricaoCentroCustoAplicacao?: string;
}

interface StockDeposit {
  codigoDeposito: string;
  nomeDeposito: string;
  estoqueAtual: number;
  estoqueMinimo: number;
  estoqueMaximo: number;
  estoqueReservado: number;
  estoqueDisponivel: number;
}

interface StockSummary {
  totalMinimo: number;
  totalMaximo: number;
  totalAtual: number;
  totalReservado: number;
  totalDisponivel: number;
}

export interface FornecedorCotacao {
  codFornecedor: number;
  nomeFornecedor: string;
  razaoSocialFornecedor?: string;
  cnpjFornecedor?: string | number;
  telefoneFornecedor?: string;
  cidadeFornecedor?: string;
  ufFornecedor?: string;
  precoCotado: number;
  quantidadeCotada: number;
  prazoEntrega: number;
  tipoFrete?: string;
  condicaoPagamento?: string;
  descricaoCondicaoPagamento?: string;
  situacaoCotacao?: number;
  numOrdemCompra?: number;
  vencedor?: number;
}

export interface AprovadorAlcada {
  sequencia: number;
  codUsuario: number;
  nomeUsuario?: string;
  situacao: string;
  dataAprovacao?: string;
  horaAprovacao?: number;
  observacao?: string;
  descricaoNivel?: string;
}

interface TrackingData {
  found: boolean;
  overallStatus: 'CRIADA' | 'EM_APROVACAO' | 'APROVADA' | 'GEROU_OC' | 'FINALIZADA' | 'REPROVADA' | 'CANCELADA';
  podeEditarObservacao?: boolean;
  observacaoSolicitacao?: string;
  criacao: {
    data: string;
    hora?: number;
    solicitante: string;
  };
  aprovacao: {
    numApr?: number | null;
    situacao: string;
    data?: string;
    hora?: number;
    aprovador?: string | null;
    aprovadores?: AprovadorAlcada[];
    motivo?: string | null;
  };
  cotacao?: {
    temCotacao: boolean;
    numCotacao?: number | null;
    numProcessoCpt?: number | null;
    dataEnvioFornecedor?: string | null;
    dataInicio?: string | null;
    dataFim?: string | null;
    comprador?: string | null;
    totalFornecedoresCotados?: number;
    totalRespostasRecebidas?: number;
    statusDescricao: string;
    fornecedores?: FornecedorCotacao[];
  };
  ordemCompra: {
    gerou: boolean;
    numero?: number | null;
    dataEmissao?: string | null;
    fornecedor?: string | null;
    quantidadePedida?: number;
    quantidadeAberta?: number;
    quantidadeRecebida?: number;
    atendida?: boolean;
    situacao?: string | null;
  };
  recebimento: {
    finalizada: boolean;
    data?: string | null;
    notaFiscal?: number | null;
    chaveNfe?: string | null;
    idEdocs?: number | string | null;
    tipoDocumentoEdocs?: string | null;
  };
  cancelamento: {
    cancelada: boolean;
    motivo?: string | null;
    data?: string | null;
    hora?: number | null;
    cancelador?: string | null;
  };
}

interface PurchaseRequestDetailsModalProps {
  open: boolean;
  onClose: () => void;
  item: PurchaseRequestDetailData | null;
  onObservationUpdated?: (newObs: string) => void;
}

const formatNumberPtBR = (num?: number) => {
  if (num === undefined || num === null || Number.isNaN(Number(num))) return '0';
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 4,
  }).format(Number(num));
};

const formatCurrencyPtBR = (num?: number) => {
  if (num === undefined || num === null || Number.isNaN(Number(num))) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Number(num));
};

const formatCnpjCpf = (val?: string | number) => {
  if (!val) return '';
  const str = String(val).replace(/\D/g, '');
  if (str.length === 14) {
    return str.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  }
  if (str.length === 11) {
    return str.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }
  return str;
};

const formatMinutesToTime = (minutes?: number) => {
  if (minutes === undefined || minutes === null) return '';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

export default function PurchaseRequestDetailsModal({
  open,
  onClose,
  item,
  onObservationUpdated,
}: PurchaseRequestDetailsModalProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [currentUserErpId, setCurrentUserErpId] = useState<number | null>(null);

  const [stockDeposits, setStockDeposits] = useState<StockDeposit[]>([]);
  const [stockSummary, setStockSummary] = useState<StockSummary | null>(null);
  const [isLoadingStock, setIsLoadingStock] = useState<boolean>(false);

  const [tracking, setTracking] = useState<TrackingData | null>(null);
  const [isLoadingTracking, setIsLoadingTracking] = useState<boolean>(false);
  const [isDownloadingDanfe, setIsDownloadingDanfe] = useState<boolean>(false);

  const [openCotacaoModal, setOpenCotacaoModal] = useState<boolean>(false);
  const [openAprovadoresModal, setOpenAprovadoresModal] = useState<boolean>(false);
  const [openCancelamentoModal, setOpenCancelamentoModal] = useState<boolean>(false);

  const [currentObservation, setCurrentObservation] = useState<string>('');
  const [isEditingObservation, setIsEditingObservation] = useState<boolean>(false);
  const [editingObservationText, setEditingObservationText] = useState<string>('');
  const [isSavingObservation, setIsSavingObservation] = useState<boolean>(false);
  const [observationError, setObservationError] = useState<string>('');

  useEffect(() => {
    const fetchAuthStatus = async () => {
      try {
        const { data } = await api.get('erp/auth/status');
        if (data?.user?.erp_id) {
          setCurrentUserErpId(Number(data.user.erp_id));
        }
      } catch (err) {
        console.error('Erro ao buscar status do usuário ERP:', err);
      }
    };
    fetchAuthStatus();
  }, []);

  useEffect(() => {
    if (open && item) {
      setCurrentObservation(item.observacaoSolicitacao || '');
      setIsEditingObservation(false);
      setObservationError('');

      const fetchTracking = async () => {
        try {
          setIsLoadingTracking(true);
          const { data } = await api.get('erp/purchases/requests/tracking', {
            params: {
              companyId: item.empresa,
              branchId: item.filial,
              requestNum: item.solicitacao,
              sequence: item.sequencia,
            },
          });
          if (data?.found) {
            setTracking(data);
            if (data.observacaoSolicitacao !== undefined) {
              setCurrentObservation(data.observacaoSolicitacao);
            }
          } else {
            setTracking(null);
          }
        } catch (err) {
          console.error('Erro ao buscar rastreamento da solicitação:', err);
          setTracking(null);
        } finally {
          setIsLoadingTracking(false);
        }
      };

      fetchTracking();

      if (item.produto) {
        const fetchStock = async () => {
          try {
            setIsLoadingStock(true);
            const { data } = await api.get('erp/purchases/requests/product-stock', {
              params: {
                companyId: item.empresa,
                productId: item.produto,
                derivation: item.derivacao || ' ',
              },
            });
            setStockDeposits(data.deposits || []);
            setStockSummary(data.summary || null);
          } catch (error) {
            console.error('Erro ao carregar estoque do produto:', error);
          } finally {
            setIsLoadingStock(false);
          }
        };
        fetchStock();
      } else {
        setStockDeposits([]);
        setStockSummary(null);
      }
    } else {
      setTracking(null);
      setStockDeposits([]);
      setStockSummary(null);
      setOpenCotacaoModal(false);
      setOpenAprovadoresModal(false);
      setOpenCancelamentoModal(false);
      setIsEditingObservation(false);
    }
  }, [open, item]);

  const handleDownloadDanfe = async (
    idEdocs?: number | string | null,
    tipo?: string | null,
    numNfc?: number | null,
  ) => {
    if (!idEdocs) {
      alert('Esta nota fiscal ainda não possui registro indexado no eDocs.');
      return;
    }

    try {
      setIsDownloadingDanfe(true);
      const response = await api.get('erp/purchases/requests/nfe-danfe', {
        params: {
          idEdocs,
          tipo: tipo || 'DANFE310_RECEBIMENTO',
          numNfc,
        },
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `DOCUMENTO_${numNfc || idEdocs}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erro ao fazer download do documento no eDocs:', error);
      alert('Não foi possível obter a DANFE/DANFSE no Senior eDocs para esta nota.');
    } finally {
      setIsDownloadingDanfe(false);
    }
  };

  const isRejected = tracking?.aprovacao.situacao === 'REP' || item?.situacaoAprovacao === 'REP';

  // Cancelada estritamente quando não for apenas reprovação de alçada
  const isCancelled =
    !isRejected &&
    (tracking?.cancelamento.cancelada ||
      item?.situacaoAprovacao === 'CAN' ||
      ((item?.quantidadeCancelada ?? 0) > 0 && item?.quantidadeCancelada === item?.quantidadeSolicitada));

  const isApproved =
    tracking?.aprovacao.situacao === 'APR' ||
    item?.situacaoAprovacao === 'APR' ||
    Boolean(tracking?.ordemCompra.gerou) ||
    Boolean(tracking?.cotacao?.temCotacao);

  const isPendingApproval = !isApproved && !isCancelled && !isRejected;

  const canEditObservation = useMemo(() => {
    if (!isPendingApproval || !item) return false;
    if (tracking?.podeEditarObservacao === true) return true;

    if (currentUserErpId) {
      if (Number(item.codUsuarioSolicitante) === currentUserErpId) return true;
      const approvers = tracking?.aprovacao?.aprovadores || [];
      if (approvers.some(ap => Number(ap.codUsuario) === currentUserErpId)) return true;
    }

    return false;
  }, [isPendingApproval, item, tracking, currentUserErpId]);

  const handleStartEditing = () => {
    setEditingObservationText(currentObservation);
    setIsEditingObservation(true);
    setObservationError('');
  };

  const handleCancelEditing = () => {
    setIsEditingObservation(false);
    setObservationError('');
  };

  const handleSaveObservation = async () => {
    if (!item) return;

    if (editingObservationText.length > 250) {
      setObservationError('A observação deve ter no máximo 250 caracteres.');
      return;
    }

    try {
      setIsSavingObservation(true);
      setObservationError('');

      await api.put('erp/purchases/requests/observation', {
        companyId: item.empresa,
        branchId: item.filial,
        requestNum: item.solicitacao,
        sequence: item.sequencia,
        observation: editingObservationText.trim(),
      });

      setCurrentObservation(editingObservationText.trim());
      setIsEditingObservation(false);
      if (onObservationUpdated) {
        onObservationUpdated(editingObservationText.trim());
      }
    } catch (err: any) {
      console.error('Erro ao atualizar observação:', err);
      const msg = err?.response?.data?.message || 'Erro ao salvar a observação no ERP.';
      setObservationError(msg);
    } finally {
      setIsSavingObservation(false);
    }
  };

  if (!item) return null;

  const motivoCancelamento =
    tracking?.cancelamento?.motivo || tracking?.aprovacao?.motivo || item.motivoCancelamento || null;

  const cancelDate =
    tracking?.cancelamento?.data || item.dataCancelamento || tracking?.aprovacao?.data || item.dataAprovacao || '';

  const cancelTime =
    tracking?.cancelamento?.hora ?? item.horaCancelamento ?? tracking?.aprovacao?.hora ?? item.horaAprovacao;

  const cancelUser =
    tracking?.cancelamento?.cancelador ||
    (item.nomeUsuarioCancelador
      ? `${item.codUsuarioCancelador ? `[${item.codUsuarioCancelador}] ` : ''}${item.nomeUsuarioCancelador}`
      : '') ||
    tracking?.aprovacao?.aprovador ||
    item.nomeUsuarioAprovador ||
    'Usuário do ERP';

  const hasOc = Boolean(tracking?.ordemCompra.gerou);
  const isOcAttended = Boolean(tracking?.ordemCompra.atendida);
  const isCompleted = Boolean(tracking?.recebimento.finalizada) || isOcAttended;

  const cotacaoInfo = tracking?.cotacao;
  const temCotacao = Boolean(cotacaoInfo?.temCotacao);
  const listaFornecedores = cotacaoInfo?.fornecedores || [];
  const listaAprovadores = tracking?.aprovacao.aprovadores || [];

  const idEdocs = tracking?.recebimento.idEdocs;
  const tipoDoc = tracking?.recebimento.tipoDocumentoEdocs;
  const numNota = tracking?.recebimento.notaFiscal;

  // ── MONTAGEM DO CICLO DE VIDA (TIMELINE PRESERVANDO PONTO DE PARADA) ──
  const steps: {
    id: 'criada' | 'aprovada' | 'cotacao' | 'recebimento' | 'cancelada';
    label: string;
    subtitle?: string;
    description?: string;
    status: 'done' | 'active' | 'waiting' | 'error';
    icon: React.ReactNode;
    clickable?: boolean;
    extraAction?: React.ReactNode;
  }[] = [];

  // 1. Etapa de Criação
  steps.push({
    id: 'criada',
    label: 'Criada',
    subtitle: item.dataSolicitacao ? `${item.dataSolicitacao} ${formatMinutesToTime(item.horaSolicitacao)}` : '',
    description: item.nomeUsuarioSolicitante || `Usuário ${item.codUsuarioSolicitante}`,
    status: 'done',
    icon: <CheckCircle fontSize="small" />,
  });

  // 2. Etapa de Aprovação
  if (isRejected) {
    // Quando reprovada: apenas Reprovada com erro. Não duplica com cancelada
    steps.push({
      id: 'aprovada',
      label: 'Reprovada',
      subtitle: cancelDate ? `${cancelDate} ${formatMinutesToTime(cancelTime)}` : '',
      description: `Por ${cancelUser}`,
      status: 'error',
      icon: <Cancel fontSize="small" />,
      clickable: true,
    });
  } else {
    steps.push({
      id: 'aprovada',
      label: isApproved ? 'Aprovada' : 'Aprovação',
      subtitle: isApproved
        ? `${tracking?.aprovacao.data || item.dataAprovacao || ''} ${formatMinutesToTime(tracking?.aprovacao.hora || item.horaAprovacao)}`
        : isCancelled
          ? 'Não Aprovada'
          : 'Em Análise',
      description: isApproved
        ? tracking?.aprovacao.aprovador || item.nomeUsuarioAprovador || 'Aprovada no ERP'
        : isCancelled
          ? 'Cancelada antes da aprovação'
          : 'Aguardando alçada',
      status: isApproved ? 'done' : isCancelled ? 'waiting' : 'active',
      icon: isApproved ? <CheckCircle fontSize="small" /> : <HourglassTop fontSize="small" />,
      clickable: true,
    });
  }

  // Se foi cancelada antes mesmo de aprovar, insere o cancelamento imediatamente e encerra
  if (isCancelled && !isApproved && !hasOc) {
    steps.push({
      id: 'cancelada',
      label: 'Cancelada',
      subtitle: cancelDate ? `${cancelDate} ${formatMinutesToTime(cancelTime)}` : 'Cancelada em Aberto',
      description: `Por ${cancelUser}`,
      status: 'error',
      icon: <Cancel fontSize="small" />,
      clickable: true,
    });
  }

  // 3. Etapa de Cotação / Ordem de Compra
  if (!isRejected && (isApproved || hasOc)) {
    let ocSubtitle = 'Aguardando';
    let ocDescription = 'Pendente de cotação';

    if (hasOc) {
      ocSubtitle = `OC #${tracking?.ordemCompra.numero}`;
      ocDescription = `${tracking?.ordemCompra.fornecedor || 'Fornecedor vinculado'} (${tracking?.ordemCompra.situacao || 'Gerada'})`;
    } else if (temCotacao) {
      ocSubtitle = `Cotação #${cotacaoInfo?.numCotacao}`;
      ocDescription = cotacaoInfo?.statusDescricao || 'Processo de cotação aberto';
    } else {
      ocSubtitle = 'Fila de Compras';
      ocDescription = 'Aguardando início da cotação';
    }

    steps.push({
      id: 'cotacao',
      label: 'Ordem de Compra',
      subtitle: ocSubtitle,
      description: ocDescription,
      status: hasOc ? 'done' : isCancelled ? 'waiting' : 'active',
      icon: hasOc ? (
        <ReceiptLong fontSize="small" />
      ) : temCotacao ? (
        <RequestQuote fontSize="small" />
      ) : (
        <ShoppingCart fontSize="small" />
      ),
      clickable: true,
    });

    // Se foi cancelada após a aprovação/cotação mas antes de ter OC atendida / faturada
    if (isCancelled && !isCompleted) {
      steps.push({
        id: 'cancelada',
        label: 'Cancelada',
        subtitle: cancelDate ? `${cancelDate} ${formatMinutesToTime(cancelTime)}` : 'Processo Cancelado',
        description: `Por ${cancelUser}`,
        status: 'error',
        icon: <Cancel fontSize="small" />,
        clickable: true,
      });
    }

    // 4. Recebimento / Finalização (Aparece se gerou OC ou foi concluída)
    if (!isCancelled || isCompleted) {
      const extraDanfeAction =
        isCompleted && idEdocs ? (
          <Button
            size="small"
            variant="contained"
            color="primary"
            startIcon={isDownloadingDanfe ? <CircularProgress size={12} color="inherit" /> : <PictureAsPdf />}
            onClick={e => {
              e.stopPropagation();
              handleDownloadDanfe(idEdocs, tipoDoc, numNota);
            }}
            disabled={isDownloadingDanfe}
            sx={{
              mt: 0.8,
              py: 0.2,
              px: 1,
              fontSize: '0.65rem',
              fontWeight: 'bold',
              textTransform: 'none',
              borderRadius: 1,
            }}
          >
            {tipoDoc === 'DANFSE_RECEBIMENTO' ? 'Baixar DANFSE (PDF)' : 'Baixar DANFE (PDF)'}
          </Button>
        ) : null;

      steps.push({
        id: 'recebimento',
        label: isCompleted ? 'Finalizada' : 'Recebimento',
        subtitle: isCompleted
          ? numNota
            ? `NF #${numNota}`
            : 'O.C. Atendida'
          : hasOc
            ? 'Aguardando Entrega'
            : 'Aguardando',
        description: isCompleted
          ? tracking?.recebimento.data
            ? `Entregue em ${tracking?.recebimento.data}`
            : 'Concluído no ERP Sapiens'
          : hasOc
            ? 'Aguardando faturamento/entrega'
            : 'Aguardando emissão da OC',
        status: isCompleted ? 'done' : hasOc ? 'active' : 'waiting',
        icon: isCompleted ? <CheckCircleOutline fontSize="small" /> : <LocalShipping fontSize="small" />,
        extraAction: extraDanfeAction,
      });
    }
  }

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle
          sx={{
            fontWeight: 'bold',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <Box>
            Detalhes da Solicitação #{item.solicitacao} (Seq {item.sequencia})
          </Box>
          {isLoadingTracking ? (
            <Chip
              icon={<CircularProgress size={14} color="inherit" />}
              label="Carregando status..."
              size="small"
              sx={{ fontWeight: 'bold' }}
            />
          ) : isCancelled ? (
            <Chip label="CANCELADA" color="error" size="small" sx={{ fontWeight: 'bold' }} />
          ) : isRejected ? (
            <Chip label="REPROVADA" color="error" size="small" sx={{ fontWeight: 'bold' }} />
          ) : isCompleted ? (
            <Chip label="FINALIZADA - OC ATENDIDA" color="success" size="small" sx={{ fontWeight: 'bold' }} />
          ) : hasOc ? (
            <Chip label="ORDEM DE COMPRA GERADA" color="info" size="small" sx={{ fontWeight: 'bold' }} />
          ) : isApproved ? (
            <Chip
              label={temCotacao ? `EM COTAÇÃO #${cotacaoInfo?.numCotacao}` : 'APROVADA - NA FILA DE COMPRAS'}
              color="primary"
              size="small"
              sx={{ fontWeight: 'bold' }}
            />
          ) : (
            <Chip label="PENDENTE DE APROVAÇÃO" color="warning" size="small" sx={{ fontWeight: 'bold' }} />
          )}
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2.5}>
            {/* 1. INFORMAÇÕES GERAIS */}
            <Box>
              <Typography
                variant="subtitle2"
                color="primary"
                fontWeight="bold"
                mb={1.5}
                display="flex"
                alignItems="center"
                gap={1}
              >
                <Assignment fontSize="small" /> Informações Gerais
              </Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Solicitante:
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {item.codUsuarioSolicitante} - {item.nomeUsuarioSolicitante || 'N/A'}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Produto / Serviço:
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    [{item.produto || item.servico}] - {item.descricaoProduto || item.descricaoServico}
                  </Typography>
                </Grid>

                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    Data Geração:
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {item.dataSolicitacao} {formatMinutesToTime(item.horaSolicitacao)}
                  </Typography>
                </Grid>

                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    Data Prevista:
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {item.dataPrevista || '-'}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    C. Custo Solicitante:
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {item.centroCusto ? `${item.centroCusto} - ${item.descricaoCentroCusto || ''}` : 'N/A'}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    C. Custo Aplicação:
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {item.centroCustoAplicacao
                      ? `${item.centroCustoAplicacao} - ${item.descricaoCentroCustoAplicacao || ''}`
                      : 'N/A'}
                  </Typography>
                </Grid>

                <Grid item xs={3}>
                  <Typography variant="caption" color="text.secondary">
                    Qtd Solicitada:
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {formatNumberPtBR(item.quantidadeSolicitada ?? item.quantidade)}
                  </Typography>
                </Grid>

                <Grid item xs={3}>
                  <Typography variant="caption" color="text.secondary">
                    Qtd Aprovada:
                  </Typography>
                  <Typography variant="body2" fontWeight="bold" color="success.main">
                    {formatNumberPtBR(item.quantidadeAprovada ?? 0)}
                  </Typography>
                </Grid>

                <Grid item xs={3}>
                  <Typography variant="caption" color="text.secondary">
                    Qtd Cancelada:
                  </Typography>
                  <Typography variant="body2" fontWeight="bold" color="error.main">
                    {formatNumberPtBR(item.quantidadeCancelada ?? 0)}
                  </Typography>
                </Grid>

                <Grid item xs={3}>
                  <Typography variant="caption" color="text.secondary">
                    Saldo Líquido:
                  </Typography>
                  <Typography variant="body2" fontWeight="bold" color="primary.main">
                    {formatNumberPtBR(item.quantidade)}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            {/* 2. OBSERVAÇÕES E JUSTIFICATIVAS */}
            <Divider />
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                <Typography
                  variant="subtitle2"
                  color="primary"
                  fontWeight="bold"
                  display="flex"
                  alignItems="center"
                  gap={1}
                >
                  <Notes fontSize="small" /> Observações e Justificativas
                </Typography>

                {canEditObservation && !isEditingObservation && (
                  <Tooltip title="Editar Observação da Solicitação">
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      startIcon={<Edit fontSize="small" />}
                      onClick={handleStartEditing}
                      sx={{ textTransform: 'none', fontWeight: 'bold', py: 0.2 }}
                    >
                      Editar Observação
                    </Button>
                  </Tooltip>
                )}
              </Box>

              {(isCancelled || isRejected) && Boolean(motivoCancelamento) && (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    mb: 2,
                    borderRadius: 2,
                    bgcolor: isDark ? alpha(theme.palette.error.main, 0.15) : '#fef2f2',
                    border: `1.5px solid ${theme.palette.error.main}`,
                  }}
                >
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    color="error.main"
                    display="flex"
                    alignItems="center"
                    gap={1}
                    gutterBottom
                  >
                    <Cancel fontSize="small" /> Motivo da Reprovação / Cancelamento:
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.primary"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      overflowWrap: 'anywhere',
                      fontWeight: 'bold',
                    }}
                  >
                    {motivoCancelamento}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                    Cancelado por: <b>{cancelUser}</b> em {cancelDate} {formatMinutesToTime(cancelTime)}
                  </Typography>
                </Paper>
              )}

              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: isDark ? 'grey.900' : 'grey.50',
                  borderColor: theme.palette.divider,
                }}
              >
                <Typography variant="body2" fontWeight="bold" color="text.primary" gutterBottom>
                  Observação da Solicitação:
                </Typography>

                {isEditingObservation ? (
                  <Box mt={1}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      size="small"
                      value={editingObservationText}
                      onChange={e => setEditingObservationText(e.target.value)}
                      error={Boolean(observationError)}
                      helperText={observationError || `${editingObservationText.length}/250 caracteres`}
                      inputProps={{ maxLength: 250 }}
                      disabled={isSavingObservation}
                    />
                    <Stack direction="row" spacing={1} justifyContent="flex-end" mt={1.5}>
                      <Button
                        size="small"
                        color="inherit"
                        variant="outlined"
                        onClick={handleCancelEditing}
                        disabled={isSavingObservation}
                        startIcon={<Close />}
                      >
                        Cancelar
                      </Button>
                      <Button
                        size="small"
                        color="primary"
                        variant="contained"
                        onClick={handleSaveObservation}
                        disabled={isSavingObservation}
                        startIcon={isSavingObservation ? <CircularProgress size={16} color="inherit" /> : <Save />}
                        sx={{ fontWeight: 'bold' }}
                      >
                        {isSavingObservation ? 'Salvando...' : 'Salvar Alteração'}
                      </Button>
                    </Stack>
                  </Box>
                ) : (
                  <Typography
                    variant="body2"
                    color="text.primary"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      overflowWrap: 'anywhere',
                      lineHeight: 1.5,
                    }}
                  >
                    {currentObservation || 'Nenhuma observação informada.'}
                  </Typography>
                )}

                {item.complementoProduto && (
                  <>
                    <Divider sx={{ my: 1.5 }} />
                    <Typography variant="body2" fontWeight="bold" color="text.primary" gutterBottom>
                      Complemento do Produto:
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.primary"
                      sx={{
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                      }}
                    >
                      {item.complementoProduto}
                    </Typography>
                  </>
                )}
              </Paper>
            </Box>

            {/* 3. TIMELINE */}
            <Divider />
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                <Typography
                  variant="subtitle2"
                  color="primary"
                  fontWeight="bold"
                  display="flex"
                  alignItems="center"
                  gap={1}
                >
                  <TimelineIcon fontSize="small" /> Ciclo de Vida da Solicitação
                </Typography>
                {isLoadingTracking && <CircularProgress size={16} />}
              </Box>

              <Paper
                elevation={0}
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: isDark ? alpha(theme.palette.grey[900], 0.6) : alpha(theme.palette.grey[100], 0.7),
                  borderColor: theme.palette.divider,
                  minHeight: 140,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {isLoadingTracking ? (
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    py={2}
                    gap={1.5}
                  >
                    <CircularProgress size={26} color="primary" />
                    <Typography variant="body2" color="text.secondary" fontWeight="500">
                      Consultando histórico no ERP Sapiens...
                    </Typography>
                  </Box>
                ) : (
                  <Box
                    display="flex"
                    flexDirection={{ xs: 'column', md: 'row' }}
                    alignItems={{ xs: 'stretch', md: 'stretch' }}
                    gap={1}
                    width="100%"
                  >
                    {steps.map((step, idx) => {
                      let badgeColor = theme.palette.grey[400];
                      let textColor = 'text.secondary';
                      let isCurrent = false;

                      if (step.status === 'done') {
                        badgeColor = theme.palette.success.main;
                        textColor = 'text.primary';
                      } else if (step.status === 'error') {
                        badgeColor = theme.palette.error.main;
                        textColor = 'error.main';
                      } else if (step.status === 'active') {
                        badgeColor = theme.palette.primary.main;
                        textColor = 'primary.main';
                        isCurrent = true;
                      }

                      const tooltipText =
                        step.id === 'cotacao'
                          ? 'Clique para ver fornecedores e cotações'
                          : step.id === 'aprovada'
                            ? isRejected
                              ? 'Clique para ver motivo e detalhes da reprovação'
                              : 'Clique para ver os aprovadores da alçada'
                            : step.id === 'cancelada'
                              ? 'Clique para ver motivo e detalhes do cancelamento'
                              : '';

                      const cardContent = (
                        <Box
                          onClick={() => {
                            if (step.clickable) {
                              if (step.id === 'cotacao') {
                                setOpenCotacaoModal(true);
                              } else if (step.id === 'aprovada') {
                                if (isRejected) {
                                  setOpenCancelamentoModal(true);
                                } else {
                                  setOpenAprovadoresModal(true);
                                }
                              } else if (step.id === 'cancelada') {
                                setOpenCancelamentoModal(true);
                              }
                            }
                          }}
                          sx={{
                            flex: 1,
                            p: 1.5,
                            borderRadius: 2,
                            bgcolor: isDark ? 'grey.900' : 'background.paper',
                            border: `1px solid ${isCurrent ? badgeColor : theme.palette.divider}`,
                            boxShadow: isCurrent ? `0 0 0 1px ${badgeColor}` : 'none',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'flex-start',
                            minHeight: 110,
                            minWidth: 0,
                            cursor: step.clickable ? 'pointer' : 'default',
                            transition: 'all 0.2s ease-in-out',
                            position: 'relative',
                            ...(step.clickable && {
                              '&:hover': {
                                transform: 'translateY(-2px)',
                                borderColor: theme.palette.primary.main,
                                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
                              },
                            }),
                          }}
                        >
                          <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Box
                                sx={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: '50%',
                                  bgcolor: alpha(badgeColor, 0.15),
                                  color: badgeColor,
                                  border: `1.5px solid ${badgeColor}`,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexShrink: 0,
                                }}
                              >
                                {step.icon}
                              </Box>
                              <Typography
                                variant="body2"
                                fontWeight="bold"
                                color={textColor}
                                sx={{ fontSize: '0.85rem', lineHeight: 1.2 }}
                              >
                                {step.label}
                              </Typography>
                            </Box>

                            {step.clickable && (
                              <Tooltip title={tooltipText}>
                                <TouchApp fontSize="small" sx={{ color: 'action.active', opacity: 0.6 }} />
                              </Tooltip>
                            )}
                          </Box>

                          <Box sx={{ pl: 0.5 }}>
                            {step.subtitle && (
                              <Typography variant="caption" color="text.primary" fontWeight="bold" display="block">
                                {step.subtitle}
                              </Typography>
                            )}
                            {step.description && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                                sx={{
                                  wordBreak: 'break-word',
                                  mt: 0.2,
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                }}
                              >
                                {step.description}
                              </Typography>
                            )}
                            {step.extraAction && <Box mt={0.5}>{step.extraAction}</Box>}
                          </Box>
                        </Box>
                      );

                      return (
                        <React.Fragment key={step.label}>
                          {cardContent}

                          {idx < steps.length - 1 && (
                            <Box
                              display="flex"
                              justifyContent="center"
                              alignItems="center"
                              sx={{
                                color: step.status === 'done' ? 'success.main' : 'action.disabled',
                                transform: { xs: 'rotate(90deg)', md: 'none' },
                                my: { xs: 0.5, md: 0 },
                                px: { xs: 0, md: 0.5 },
                                flexShrink: 0,
                                alignSelf: 'center',
                              }}
                            >
                              <ArrowForward fontSize="small" />
                            </Box>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </Box>
                )}
              </Paper>
            </Box>

            {/* 4. DETALHES DA ORDEM DE COMPRA */}
            {hasOc && (
              <>
                <Divider />
                <Box>
                  <Typography
                    variant="subtitle2"
                    color="primary"
                    fontWeight="bold"
                    mb={1.5}
                    display="flex"
                    alignItems="center"
                    gap={1}
                  >
                    <ReceiptLong fontSize="small" /> Detalhes da Ordem de Compra
                  </Typography>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" color="text.secondary">
                        Número da OC:
                      </Typography>
                      <Typography variant="body2" fontWeight="bold" color="primary.main">
                        Nº {tracking?.ordemCompra.numero}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" color="text.secondary">
                        Data Emissão:
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {tracking?.ordemCompra.dataEmissao || '-'}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" color="text.secondary">
                        Situação no ERP:
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        color={isOcAttended ? 'success.main' : 'text.primary'}
                      >
                        {tracking?.ordemCompra.situacao || (isOcAttended ? 'O.C. Atendida' : 'Aberta')}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={8}>
                      <Typography variant="caption" color="text.secondary">
                        Fornecedor Vinculado:
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {tracking?.ordemCompra.fornecedor || 'Não informado'}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" color="text.secondary">
                        Qtd Pedida / Aberta:
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {formatNumberPtBR(tracking?.ordemCompra.quantidadePedida)} /{' '}
                        {formatNumberPtBR(tracking?.ordemCompra.quantidadeAberta)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </>
            )}

            {/* 5. POSIÇÃO DE ESTOQUE POR DEPÓSITO */}
            <Divider />
            <Box>
              <Typography
                variant="subtitle2"
                color="primary"
                fontWeight="bold"
                mb={1.5}
                display="flex"
                alignItems="center"
                gap={1}
              >
                <Inventory fontSize="small" /> Posição de Estoque por Depósito
              </Typography>

              {isLoadingStock ? (
                <Box display="flex" alignItems="center" gap={1.5} py={2}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    Buscando estoque no Sapiens...
                  </Typography>
                </Box>
              ) : !item.produto ? (
                <Typography variant="body2" color="text.secondary">
                  Item é um Serviço. Não possui controle de estoque por depósito.
                </Typography>
              ) : stockDeposits.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Nenhum depósito com saldo ou movimentação de estoque para este produto.
                </Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: isDark ? 'grey.900' : 'grey.100' }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>Depósito</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                          Est. Mínimo
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                          Est. Máximo
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                          Est. Atual
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                          Est. Reservado
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                          Est. Disponível
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {stockDeposits.map(dep => (
                        <TableRow key={dep.codigoDeposito} hover>
                          <TableCell sx={{ fontWeight: 'bold' }}>
                            {dep.codigoDeposito} - {dep.nomeDeposito || 'Depósito'}
                          </TableCell>
                          <TableCell align="right">{formatNumberPtBR(dep.estoqueMinimo)}</TableCell>
                          <TableCell align="right">{formatNumberPtBR(dep.estoqueMaximo)}</TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontWeight: 'bold',
                              color: dep.estoqueAtual > 0 ? 'success.main' : 'text.primary',
                            }}
                          >
                            {formatNumberPtBR(dep.estoqueAtual)}
                          </TableCell>
                          <TableCell align="right" sx={{ color: 'warning.main', fontWeight: 'bold' }}>
                            {formatNumberPtBR(dep.estoqueReservado)}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontWeight: 'bold',
                              color: dep.estoqueDisponivel > 0 ? 'success.main' : 'error.main',
                            }}
                          >
                            {formatNumberPtBR(dep.estoqueDisponivel)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>

                    <TableFooter>
                      <TableRow
                        sx={{
                          bgcolor: isDark
                            ? alpha(theme.palette.primary.main, 0.15)
                            : alpha(theme.palette.primary.light, 0.2),
                        }}
                      >
                        <TableCell sx={{ fontWeight: 'bold' }}>TOTAL GERAL</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                          {formatNumberPtBR(stockSummary?.totalMinimo)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                          {formatNumberPtBR(stockSummary?.totalMaximo)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                          {formatNumberPtBR(stockSummary?.totalAtual)}
                        </TableCell>
                        <TableCell align="right" sx={{ color: 'warning.main', fontWeight: 'bold' }}>
                          {formatNumberPtBR(stockSummary?.totalReservado)}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontWeight: 'bold',
                            color: (stockSummary?.totalDisponivel ?? 0) > 0 ? 'success.main' : 'error.main',
                          }}
                        >
                          {formatNumberPtBR(stockSummary?.totalDisponivel)}
                        </TableCell>
                      </TableRow>
                    </TableFooter>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose} color="inherit">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* MODAL DE DETALHES DO CANCELAMENTO */}
      <Dialog open={openCancelamentoModal} onClose={() => setOpenCancelamentoModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: 'error.main',
          }}
        >
          <Cancel /> Detalhes do Cancelamento da Solicitação #{item.solicitacao}
        </DialogTitle>

        <DialogContent dividers sx={{ p: 2.5 }}>
          <Stack spacing={2}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: isDark ? 'grey.900' : 'grey.50',
              }}
            >
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Responsável pelo Cancelamento:
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {cancelUser}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Data e Hora:
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {cancelDate ? `${cancelDate} às ${formatMinutesToTime(cancelTime)}` : '-'}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Item:
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    [{item.produto || item.servico}] - {item.descricaoProduto || item.descricaoServico}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Quantidade Cancelada:
                  </Typography>
                  <Typography variant="body2" fontWeight="bold" color="error.main">
                    {formatNumberPtBR(item.quantidadeCancelada || item.quantidadeSolicitada || item.quantidade)}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: isDark ? alpha(theme.palette.error.main, 0.12) : '#fef2f2',
                border: `1.5px solid ${theme.palette.error.main}`,
              }}
            >
              <Typography
                variant="subtitle2"
                fontWeight="bold"
                color="error.main"
                display="flex"
                alignItems="center"
                gap={1}
                gutterBottom
              >
                <Notes fontSize="small" /> Motivo / Justificativa Informada:
              </Typography>
              <Typography
                variant="body2"
                color="text.primary"
                sx={{
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  overflowWrap: 'anywhere',
                  fontWeight: 'bold',
                }}
              >
                {motivoCancelamento || 'Nenhuma justificativa informada no ERP Sapiens.'}
              </Typography>
            </Paper>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenCancelamentoModal(false)} variant="contained" color="primary">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* MODAL DE ALÇADA DE APROVADORES */}
      <Dialog open={openAprovadoresModal} onClose={() => setOpenAprovadoresModal(false)} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: 'primary.main',
          }}
        >
          <HowToReg /> Alçada de Aprovação da Solicitação #{item.solicitacao}
        </DialogTitle>

        <DialogContent dividers sx={{ p: 2 }}>
          <Stack spacing={2}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: isDark ? 'grey.900' : 'grey.50',
              }}
            >
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">
                    Situação Atual:
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {tracking?.aprovacao.situacao === 'APR'
                      ? 'Aprovada'
                      : tracking?.aprovacao.situacao === 'REP'
                        ? 'Reprovada'
                        : tracking?.aprovacao.situacao === 'CAN'
                          ? 'Cancelada'
                          : 'Em Análise (Aguardando Alçada)'}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">
                    Aprovador Efetivo:
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {tracking?.aprovacao.aprovador || 'Pendente de aprovação'}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">
                    Controle de Aprovação (ERP):
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {tracking?.aprovacao.numApr ? `Nº ${tracking.aprovacao.numApr}` : 'Direta / Padrão'}
                  </Typography>
                </Grid>

                {motivoCancelamento && (
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="caption" color="error.main" fontWeight="bold">
                      Motivo da Reprovação / Cancelamento:
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.primary"
                      fontWeight="bold"
                      sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                    >
                      {motivoCancelamento}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Paper>

            {listaAprovadores.length === 0 ? (
              <Box py={4} textAlign="center">
                <Typography variant="body1" color="text.secondary">
                  {tracking?.aprovacao.situacao === 'APR'
                    ? 'Esta solicitação foi aprovada diretamente no ERP Sapiens sem alçada multinível configurada.'
                    : 'Nenhum nível específico de alçada cadastrado na rotina de compras para esta solicitação.'}
                </Typography>
              </Box>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5, maxHeight: 380 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={{ '& th': { bgcolor: isDark ? 'grey.900' : 'grey.200', fontWeight: 'bold' } }}>
                      <TableCell align="center">Nível / Seq</TableCell>
                      <TableCell>Aprovador Responsável</TableCell>
                      <TableCell align="center">Situação</TableCell>
                      <TableCell align="center">Data / Hora</TableCell>
                      <TableCell>Observação / Motivo</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {listaAprovadores.map((apr, idx) => {
                      const sit = (apr.situacao || '').toUpperCase();
                      const isApprovedUser = sit === 'APR' || sit === 'A';
                      const isRejectedUser = sit === 'REP' || sit === 'R';
                      const isPendingUser = sit === 'ANA' || !sit;

                      return (
                        <TableRow
                          key={`${apr.codUsuario}-${apr.sequencia}-${idx}`}
                          hover
                          sx={{
                            bgcolor: isApprovedUser
                              ? alpha(theme.palette.success.main, isDark ? 0.15 : 0.08)
                              : isRejectedUser
                                ? alpha(theme.palette.error.main, isDark ? 0.15 : 0.08)
                                : 'inherit',
                          }}
                        >
                          <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                            {apr.descricaoNivel || `Nível ${apr.sequencia || idx + 1}`}
                          </TableCell>

                          <TableCell>
                            <Box display="flex" alignItems="center" gap={1}>
                              {isApprovedUser && <CheckCircle color="success" fontSize="small" />}
                              {isRejectedUser && <Cancel color="error" fontSize="small" />}
                              {isPendingUser && <HourglassTop color="warning" fontSize="small" />}
                              <Typography variant="body2" fontWeight="bold">
                                [{apr.codUsuario}] {apr.nomeUsuario || 'Usuário do ERP'}
                              </Typography>
                            </Box>
                          </TableCell>

                          <TableCell align="center">
                            {isApprovedUser ? (
                              <Chip
                                label="Aprovou"
                                color="success"
                                size="small"
                                sx={{ fontWeight: 'bold', height: 22, fontSize: '0.7rem' }}
                              />
                            ) : isRejectedUser ? (
                              <Chip
                                label="Reprovou"
                                color="error"
                                size="small"
                                sx={{ fontWeight: 'bold', height: 22, fontSize: '0.7rem' }}
                              />
                            ) : (
                              <Chip
                                label="Aguardando"
                                color="warning"
                                variant="outlined"
                                size="small"
                                sx={{ fontWeight: 'bold', height: 22, fontSize: '0.7rem' }}
                              />
                            )}
                          </TableCell>

                          <TableCell align="center">
                            <Typography variant="caption" display="block" fontWeight="500">
                              {apr.dataAprovacao
                                ? `${apr.dataAprovacao} ${formatMinutesToTime(apr.horaAprovacao)}`
                                : '-'}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                            >
                              {apr.observacao || motivoCancelamento || '-'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenAprovadoresModal(false)} variant="contained" color="primary">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* MODAL DE DETALHAMENTO DA COTAÇÃO */}
      <Dialog open={openCotacaoModal} onClose={() => setOpenCotacaoModal(false)} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: 'primary.main',
          }}
        >
          <RequestQuote /> Detalhes da Cotação {temCotacao ? `#${cotacaoInfo?.numCotacao}` : ''}
        </DialogTitle>

        <DialogContent dividers sx={{ p: 2 }}>
          <Stack spacing={2}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: isDark ? 'grey.900' : 'grey.50',
              }}
            >
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">
                    Status no Setor de Compras:
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {cotacaoInfo?.statusDescricao || 'Aguardando início'}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">
                    Comprador Responsável:
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {cotacaoInfo?.comprador || 'Não atribuído'}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">
                    Fornecedores Cotados:
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {cotacaoInfo?.totalRespostasRecebidas ?? 0} respondida(s) de{' '}
                    {cotacaoInfo?.totalFornecedoresCotados ?? 0} cotado(s)
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Data de Início da Cotação:
                  </Typography>
                  <Typography variant="body2" fontWeight="bold" color="primary.main">
                    {cotacaoInfo?.dataInicio || cotacaoInfo?.dataEnvioFornecedor || 'Não iniciada'}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Data de Finalização da Cotação:
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    color={cotacaoInfo?.dataFim ? 'success.main' : 'text.secondary'}
                  >
                    {cotacaoInfo?.dataFim || 'Em andamento (Aguardando conclusão)'}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            {listaFornecedores.length === 0 ? (
              <Box py={4} textAlign="center">
                <Typography variant="body1" color="text.secondary">
                  {temCotacao
                    ? 'A cotação está aberta, mas nenhum fornecedor foi registrado até o momento.'
                    : 'Esta solicitação ainda não possui processo de cotação aberto no ERP Sapiens.'}
                </Typography>
              </Box>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5, maxHeight: 380 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={{ '& th': { bgcolor: isDark ? 'grey.900' : 'grey.200', fontWeight: 'bold' } }}>
                      <TableCell>Fornecedor</TableCell>
                      <TableCell align="right">Preço Unitário</TableCell>
                      <TableCell align="right">Qtd. Cotada</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell>Cond. Pagto / Frete</TableCell>
                      <TableCell align="center">Prazo</TableCell>
                      <TableCell align="center">Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {listaFornecedores.map((forn, idx) => {
                      const hasPrice = Number(forn.precoCotado) > 0;
                      const isWinner = forn.vencedor === 1 && hasPrice;
                      const totalCotado = Number(forn.precoCotado) * (Number(forn.quantidadeCotada) || item.quantidade);

                      return (
                        <TableRow
                          key={`${forn.codFornecedor}-${idx}`}
                          hover
                          sx={{
                            bgcolor: isWinner ? alpha(theme.palette.success.main, isDark ? 0.15 : 0.08) : 'inherit',
                          }}
                        >
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={1}>
                              {isWinner && (
                                <Tooltip title="Proposta Vencedora da Cotação">
                                  <Star color="success" fontSize="small" />
                                </Tooltip>
                              )}
                              <Box>
                                <Typography variant="body2" fontWeight="bold">
                                  [{forn.codFornecedor}] {forn.nomeFornecedor}
                                </Typography>
                                {forn.cnpjFornecedor && (
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    CNPJ: {formatCnpjCpf(forn.cnpjFornecedor)}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </TableCell>

                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                            {hasPrice ? (
                              <Typography
                                variant="body2"
                                fontWeight="bold"
                                color={isWinner ? 'success.main' : 'text.primary'}
                              >
                                {formatCurrencyPtBR(forn.precoCotado)}
                              </Typography>
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                Sem resposta
                              </Typography>
                            )}
                          </TableCell>

                          <TableCell align="right">
                            {formatNumberPtBR(forn.quantidadeCotada || item.quantidade)}
                          </TableCell>

                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                            {hasPrice ? formatCurrencyPtBR(totalCotado) : '-'}
                          </TableCell>

                          <TableCell>
                            <Typography variant="caption" color="text.primary" display="block">
                              {forn.descricaoCondicaoPagamento || forn.condicaoPagamento || 'Não informada'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Frete: {forn.tipoFrete === 'C' ? 'CIF' : forn.tipoFrete === 'F' ? 'FOB' : 'Não def.'}
                            </Typography>
                          </TableCell>

                          <TableCell align="center">
                            <Typography variant="caption">
                              {hasPrice ? (forn.prazoEntrega > 0 ? `${forn.prazoEntrega} dia(s)` : 'Imediato') : '-'}
                            </Typography>
                          </TableCell>

                          <TableCell align="center">
                            {isWinner ? (
                              <Chip
                                label={forn.numOrdemCompra ? `Vencedor (OC #${forn.numOrdemCompra})` : 'Vencedor'}
                                color="success"
                                size="small"
                                sx={{ fontWeight: 'bold', height: 22, fontSize: '0.7rem' }}
                              />
                            ) : hasPrice ? (
                              <Chip label="Cotado" size="small" sx={{ height: 22, fontSize: '0.7rem' }} />
                            ) : (
                              <Chip
                                label="Pendente"
                                color="warning"
                                variant="outlined"
                                size="small"
                                sx={{ height: 22, fontSize: '0.7rem' }}
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenCotacaoModal(false)} variant="contained" color="primary">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
